import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Bookmark, ArrowRight, Download, Upload, RefreshCw, Cloud, CloudOff, Trash2, LayoutList, LayoutGrid, AlertCircle, Clock, ExternalLink, ArrowUpDown, Users, Plus } from 'lucide-react';
import { format, formatDistanceToNowStrict, differenceInDays } from 'date-fns';
import { useBookmarks } from '../contexts/BookmarkContext';
import { bookmarksAPI, forgeAPI } from '../services/api';
import { useAnalysisGlobal } from '../contexts/AnalysisContext';
import SavedTenderCard, { getPriceEstimation, formatCurrency } from '../components/SavedTenderCard';
import TenderFilterChips from '../components/TenderFilterChips';
import { applyTenderFilters, FILTER_DEFAULTS, isTenderClosed } from '../utils/applyTenderFilters';
import { statusMeta } from './Forge/sourcingStatus';
import './MyTenders.css';

// Pipeline columns for Kanban board
const PIPELINE_COLUMNS = [
  { id: 'reviewing',    label: 'Reviewing',    color: '#6366f1' },
  { id: 'analyzing',    label: 'Analyzing',    color: '#3b82f6' },
  { id: 'bid_decision', label: 'Bid / No-Bid', color: '#f59e0b' },
  { id: 'preparing',    label: 'Preparing',    color: '#10b981' },
  { id: 'submitted',    label: 'Submitted',    color: '#6b7280' },
];

// Analysis phases for progress bars
const ANALYSIS_PHASES = [
  { id: 'phase0', name: 'Basic Info' },
  { id: 'phase1', name: 'Qualification' },
  { id: 'phase2', name: 'Arbitrage' },
  { id: 'phase3', name: 'Sourcing' },
];

const MyTenders = () => {
  const {
    bookmarks,
    loading,
    syncing,
    syncStatus,
    isAuthenticated,
    syncError,
    addBookmark,
    isBookmarked,
    clearAllBookmarks,
    exportBookmarks,
    importBookmarks,
    refreshFromBackend,
    updateBookmarkFields
  } = useBookmarks();

  const { analyses, startAnalysis, initAnalysis } = useAnalysisGlobal();
  const navigate = useNavigate();

  // Unified team-wide view (CEO): the list/counts show the WHOLE team's saved +
  // analyzed tenders (get_all_bookmarks, with team-wide analysis_status). The
  // per-user analysis sync + actions below keep using `bookmarks` (your own), so
  // we never PATCH bookmarks you don't own (that caused the earlier storm).
  const [teamFeed, setTeamFeed] = useState(null);
  // Which tenders are currently in FORGE (for the "In FORGE" badge + remove action).
  const [forgeIds, setForgeIds] = useState(() => new Set());
  // tender_id -> FORGE sourcing status (preparing/sourcing/quoting/review/ready/submitted)
  const [forgeStatus, setForgeStatus] = useState(() => ({}));
  const loadForgeIds = useCallback(() => {
    forgeAPI.listTenders()
      .then((d) => {
        const list = d?.tenders || [];
        setForgeIds(new Set(list.map((t) => t.tender_id)));
        setForgeStatus(Object.fromEntries(list.map((t) => [t.tender_id, t.sourcing_status])));
      })
      .catch(() => {});
  }, []);
  useEffect(() => { loadForgeIds(); }, [loadForgeIds]);
  const handleRemoveFromForge = useCallback((id) => {
    forgeAPI.removeFromForge(id).then(loadForgeIds).catch(() => {});
  }, [loadForgeIds]);
  const loadTeamFeed = useCallback(async () => {
    try {
      const all = await bookmarksAPI.getBookmarks('team');
      const norm = (b) => ({
        ...b,
        id: b.id || b.tender_id,
        analysisStatus: b.analysis_status || b.analysisStatus || null,
        analyzedAt: b.analyzed_at || b.analyzedAt || null,
        pipelineStage: b.pipeline_stage || b.pipelineStage || 'reviewing',
      });
      setTeamFeed(Array.isArray(all) ? all.map(norm) : []);
    } catch (e) {
      console.error('Error loading team feed:', e);
    }
  }, []);
  // Load the team feed on mount + whenever auth resolves (dev-mode has no token
  // but the endpoint is open locally; on dev it re-fires once authenticated).
  useEffect(() => { loadTeamFeed(); }, [isAuthenticated, loadTeamFeed]);
  // Display source = team feed (falls back to your own until it loads).
  const displayBookmarks = teamFeed || bookmarks;

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [importStatus, setImportStatus] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [analyzedSearch, setAnalyzedSearch] = useState('');
  const [analyzedSort, setAnalyzedSort] = useState('newest'); // newest | oldest | az | za | rec
  const [listFilter, setListFilter] = useState('all'); // all | analyzed | saved
  const [teamBookmarks, setTeamBookmarks] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamError, setTeamError] = useState(null);
  const [teamMemberFilter, setTeamMemberFilter] = useState('all');
  // Dashboard-style filter chips shared between List + Team views. Filter
  // is applied client-side via applyTenderFilters() since both lists are
  // already loaded; nothing hits the backend.
  const [listFilters, setListFilters] = useState({ ...FILTER_DEFAULTS });
  const [teamFilters, setTeamFilters] = useState({ ...FILTER_DEFAULTS });
  const fileInputRef = useRef(null);
  const itemsPerPage = 12;

  const handleExport = async () => {
    const result = await exportBookmarks();
    if (!result.success) {
      alert('Failed to export bookmarks: ' + result.error);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImportStatus('importing');
    const result = await importBookmarks(file);

    if (result.success) {
      setImportStatus(`Imported ${result.imported} bookmarks (${result.skipped} skipped)`);
      setTimeout(() => setImportStatus(null), 3000);
    } else {
      setImportStatus('Import failed: ' + result.error);
      setTimeout(() => setImportStatus(null), 5000);
    }

    e.target.value = '';
  };

  const handleRefresh = async () => {
    await refreshFromBackend();
  };

  // Load team bookmarks when switching to team view
  const loadTeamBookmarks = async () => {
    setTeamLoading(true);
    setTeamError(null);
    try {
      const data = await bookmarksAPI.getTeamBookmarks();
      setTeamBookmarks(data);
    } catch (err) {
      console.error('Error loading team bookmarks:', err);
      setTeamError('Failed to load team feed');
    } finally {
      setTeamLoading(false);
    }
  };

  useEffect(() => {
    if (viewMode === 'team') {
      loadTeamBookmarks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  const handleToggleExpand = (tenderId) => {
    setExpandedId(prev => prev === tenderId ? null : tenderId);
  };

  const handleNotesChange = (tenderId, notes) => {
    updateBookmarkFields(tenderId, { notes });
  };

  // Map global AnalysisContext state → format SavedTenderCard expects
  const getAnalysisStateForCard = (tenderId) => {
    const a = analyses[tenderId];
    if (!a || a.status === 'idle') return null;
    return {
      status: a.status,
      completedPhases: a.completedPhases || [],
      currentPhase: a.runningPhases?.[0] || null,
      error: a.error,
    };
  };

  // Run analysis via global context (fire-and-forget + polling)
  const handleRunAnalysis = (tenderId) => {
    updateBookmarkFields(tenderId, { pipelineStage: 'analyzing' });
    startAnalysis(tenderId);
  };

  // Only resume polling for tenders that are *actively analyzing* — initing all
  // of them fired a getAnalysisStatus per bookmark (hundreds of requests) that
  // saturated the browser's ~6-connection-per-host limit and stalled the whole
  // page (incl. the team feed / intelligence calls). Completed/idle status comes
  // from the team feed's persisted analysis_status, so no poll is needed for those.
  useEffect(() => {
    displayBookmarks.forEach(b => {
      const stage = b.pipelineStage || b.pipeline_stage;
      if (stage === 'analyzing') {
        const tid = b.id || b.tender_id || b.reference_number;
        if (tid) initAnalysis(tid);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayBookmarks.length]);

  // Sync bookmark fields when analysis completes via polling.
  // Skip bookmarks already marked complete to avoid a PATCH storm on mount.
  const prevAnalysesRef = useRef({});
  useEffect(() => {
    const prev = prevAnalysesRef.current;
    Object.entries(analyses).forEach(([tid, a]) => {
      if (a.status === 'complete' && prev[tid]?.status !== 'complete') {
        // Don't re-PATCH if the bookmark already knows it's complete
        const bm = bookmarks.find(b => b.id === tid || b.tender_id === tid);
        if (bm?.analysisStatus === 'complete') return;

        const bidRec = a.phases?.phase1?.bid_recommendation ||
                       a.phases?.phase2?.bid_recommendation || null;
        updateBookmarkFields(tid, {
          analysisStatus: 'complete',
          analyzedAt: new Date().toISOString(),
          bidRecommendation: bidRec,
          pipelineStage: 'bid_decision',
        });
      } else if (a.status === 'running' && prev[tid]?.status !== 'running') {
        updateBookmarkFields(tid, { pipelineStage: 'analyzing' });
      }
    });
    prevAnalysesRef.current = { ...analyses };
  }, [analyses, updateBookmarkFields, bookmarks]);

  // Analyzed tenders for the right panel shortcut (team-wide display source)
  const analyzedTenders = displayBookmarks.filter(b => b.analysisStatus === 'complete');

  // Left panel shows the team's bookmarks, filtered by search + analyzed/saved
  // tab + dashboard-style chip filters (status / categories / dates).
  const filteredBookmarks = applyTenderFilters(displayBookmarks, listFilters)
    .filter(tender => !isTenderClosed(tender))  // hide closed/expired tenders from My Tenders
    .filter(tender => {
    // All / Analyzed / Saved tab
    if (listFilter === 'analyzed' && tender.analysisStatus !== 'complete') return false;
    if (listFilter === 'saved' && tender.analysisStatus === 'complete') return false;

    // Search filter
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      tender.title?.toLowerCase().includes(search) ||
      tender.tender_id?.toLowerCase().includes(search) ||
      tender.reference_number?.toLowerCase().includes(search) ||
      (typeof tender.organization === 'string' ? tender.organization : tender.organization?.contracting_entity || '')
        .toLowerCase().includes(search)
    );
  });

  // Pagination (list view only)
  const totalPages = Math.ceil(filteredBookmarks.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedBookmarks = filteredBookmarks.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, listFilter]);

  // Kanban drag handlers
  const handleDragStart = (e, tenderId) => {
    e.dataTransfer.setData('text/plain', tenderId);
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.classList.add('dragging');
  };

  const handleDragEnd = (e) => {
    e.currentTarget.classList.remove('dragging');
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    e.currentTarget.classList.add('drag-over');
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove('drag-over');
  };

  const handleDrop = (e, columnId) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');
    const tenderId = e.dataTransfer.getData('text/plain');
    if (tenderId) {
      updateBookmarkFields(tenderId, { pipelineStage: columnId });
    }
  };

  // Get recommendation badge from bookmark fields
  const getRecommendation = (tender) => {
    const rec = tender.bidRecommendation || tender.bid_recommendation;
    if (!rec) return null;
    const recLower = (typeof rec === 'string' ? rec : '').toLowerCase();
    if (recLower.includes('bid') && !recLower.includes('no')) return 'bid';
    if (recLower.includes('consider') || recLower.includes('maybe')) return 'consider';
    if (recLower.includes('pass') || recLower.includes('no')) return 'pass';
    return null;
  };

  // Closing date helpers
  const getClosingDate = (tender) => {
    return tender.closing_date || tender.closingDate || tender.deadline ||
      tender.end_date || tender.endDate || tender.close_date ||
      tender.closeDate || tender.bid_deadline || tender.bidDeadline || null;
  };

  const getDaysUntilClose = (closingDate) => {
    if (!closingDate) return null;
    try {
      return differenceInDays(new Date(closingDate), new Date());
    } catch {
      return null;
    }
  };

  const getUrgencyClass = (days) => {
    if (days === null) return '';
    if (days <= 0) return 'urgency-passed';
    if (days <= 3) return 'urgency-critical';
    if (days <= 7) return 'urgency-warning';
    return 'urgency-normal';
  };

  const formatClosingLabel = (closingDate) => {
    if (!closingDate) return '';
    try {
      const days = getDaysUntilClose(closingDate);
      if (days === null) return '';
      if (days <= 0) return 'Closed';
      if (days <= 7) {
        return `Closes in ${formatDistanceToNowStrict(new Date(closingDate), { unit: 'day' })}`;
      }
      const normalized = closingDate.length === 10 ? closingDate + 'T00:00:00' : closingDate;
      return `Closes ${format(new Date(normalized), 'MMM d')}`;
    } catch {
      return '';
    }
  };

  const getOrgName = (tender) => {
    return tender.organization?.contracting_entity ||
      tender.organization?.name ||
      (typeof tender.organization === 'string' ? tender.organization : '') ||
      '';
  };

  // Sync status indicator
  const getSyncIndicator = () => {
    if (!isAuthenticated) {
      return <span className="sync-badge local"><CloudOff size={12} /> Local only</span>;
    }
    switch (syncStatus) {
      case 'syncing':
        return <span className="sync-badge syncing"><RefreshCw size={12} className="spinning" /> Syncing...</span>;
      case 'synced':
        return <span className="sync-badge synced"><Cloud size={12} /> Synced</span>;
      case 'error':
        return <span className="sync-badge error"><AlertCircle size={12} /> Sync failed</span>;
      default:
        return <span className="sync-badge synced"><Cloud size={12} /> Cloud</span>;
    }
  };

  // Progress bar for kanban card
  const KanbanProgressBar = ({ tenderId, tender }) => {
    const state = getAnalysisStateForCard(tenderId);
    if (!state && tender.analysisStatus !== 'complete') return null;

    const completedPhases = state?.completedPhases || [];
    const currentPhase = state?.currentPhase;
    const isFullyComplete = tender.analysisStatus === 'complete' || state?.status === 'complete';

    return (
      <div className="kanban-card-progress">
        {ANALYSIS_PHASES.map((phase) => {
          const isComplete = isFullyComplete || completedPhases.includes(phase.id);
          const isCurrent = currentPhase === phase.id;
          return (
            <div
              key={phase.id}
              className={`progress-segment ${isComplete ? 'complete' : ''} ${isCurrent ? 'current' : ''}`}
            />
          );
        })}
      </div>
    );
  };

  // Team Feed component
  const TeamFeed = () => {
    if (teamLoading) {
      return (
        <div className="team-feed">
          <div className="team-feed-loading">
            <div className="loading-spinner"></div>
            <p>Loading team activity...</p>
          </div>
        </div>
      );
    }

    if (teamError) {
      return (
        <div className="team-feed">
          <div className="team-feed-empty">
            <AlertCircle size={32} />
            <p>{teamError}</p>
            <button className="team-retry-btn" onClick={loadTeamBookmarks}>Retry</button>
          </div>
        </div>
      );
    }

    if (teamBookmarks.length === 0) {
      return (
        <div className="team-feed">
          <div className="team-feed-empty">
            <Users size={32} />
            <p>No team activity yet</p>
            <span>When team members save tenders, they'll appear here</span>
          </div>
        </div>
      );
    }

    // Extract unique team members for the filter (sorted, deduped)
    const teamMembers = [...new Set(teamBookmarks.map(tb => tb.user_name || 'Team Member'))].sort();

    // Client-side filtering — same applyTenderFilters helper as List view,
    // plus the team-specific member chip integrated into TenderFilterChips.
    const filteredTeam = applyTenderFilters(teamBookmarks, teamFilters);

    return (
      <div className="team-feed">
        <div className="team-feed-header">
          <span>
            {filteredTeam.length} tender{filteredTeam.length !== 1 ? 's' : ''}
            {teamFilters.member !== 'all' ? ` by ${teamFilters.member}` : ' saved by team'}
          </span>
          <div className="team-feed-header-actions">
            <button className="team-refresh-btn" onClick={loadTeamBookmarks} title="Refresh">
              <RefreshCw size={14} />
            </button>
          </div>
        </div>

        {/* Dashboard-style chip row + member dropdown */}
        <div className="saved-filter-row">
          <TenderFilterChips
            filters={teamFilters}
            onChange={setTeamFilters}
            members={teamMembers}
            memberLabel="Saved by"
            resultsCount={filteredTeam.length}
            resultsLabel="tender"
          />
        </div>
        {filteredTeam.map(tb => {
          const tenderId = tb.tender_id || tb.id;
          const userName = tb.user_name || 'Team Member';
          const initial = userName.charAt(0).toUpperCase();
          const alreadySaved = isBookmarked(tenderId);
          const hasAnalysis = tb.analysis_status === 'complete' || tb.analysisStatus === 'complete';
          const closingDate = tb.closing_date || tb.closingDate;

          let timeAgo = '';
          try {
            if (tb.bookmarked_at || tb.bookmarkedAt) {
              timeAgo = formatDistanceToNowStrict(new Date(tb.bookmarked_at || tb.bookmarkedAt), { addSuffix: true });
            }
          } catch { /* ignore */ }

          const orgName = typeof tb.organization === 'object'
            ? tb.organization?.contracting_entity || tb.organization?.name || ''
            : tb.organization || '';

          return (
            <div key={`${tenderId}-${tb.user_id}`} className="team-card">
              <div className="team-card-attribution">
                <span className="team-card-avatar">{initial}</span>
                <span className="team-card-saved-by">
                  Saved by <strong>{userName}</strong>
                </span>
                {timeAgo && <span className="team-card-time">{timeAgo}</span>}
              </div>

              <div
                className="team-card-body"
                onClick={() => navigate(`/tender/${encodeURIComponent(tenderId)}`)}
              >
                <div className="team-card-title">{tb.title || 'Untitled'}</div>
                {orgName && <div className="team-card-org">{orgName}</div>}
                <div className="team-card-meta">
                  {closingDate && (
                    <span className="team-card-date">
                      <Clock size={11} />
                      {formatClosingLabel(closingDate)}
                    </span>
                  )}
                  {hasAnalysis && (
                    <span className="team-card-analyzed-badge">Analyzed</span>
                  )}
                </div>
              </div>

              <div className="team-card-actions">
                {alreadySaved ? (
                  <span className="team-card-already-saved">
                    <Bookmark size={13} /> In My Tenders
                  </span>
                ) : (
                  <button
                    className="team-card-add-btn"
                    onClick={() => addBookmark({
                      tender_id: tenderId,
                      title: tb.title,
                      organization: orgName,
                      status: tb.status,
                      closing_date: closingDate,
                    })}
                  >
                    <Plus size={13} /> Add to My Tenders
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Kanban Board component
  const KanbanBoard = () => {
    const columns = {};
    PIPELINE_COLUMNS.forEach(col => { columns[col.id] = []; });

    filteredBookmarks.forEach(tender => {
      const stage = tender.pipelineStage || 'reviewing';
      if (columns[stage]) {
        columns[stage].push(tender);
      } else {
        columns['reviewing'].push(tender);
      }
    });

    return (
      <div className="kanban-board">
        {PIPELINE_COLUMNS.map(col => (
          <div
            key={col.id}
            className="kanban-column"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, col.id)}
          >
            <div className="kanban-column-header">
              <div className="kanban-column-title-row">
                <span className="kanban-column-dot" style={{ background: col.color }} />
                <span className="kanban-column-title">{col.label}</span>
              </div>
              <span className="kanban-column-count">({columns[col.id].length})</span>
            </div>
            <div className="kanban-column-body">
              {columns[col.id].length === 0 ? (
                <div className="kanban-empty">Drop tenders here</div>
              ) : (
                columns[col.id].map(tender => {
                  const tenderId = tender.id || tender.tender_id || tender.reference_number;
                  const rec = getRecommendation(tender);
                  const closingDate = getClosingDate(tender);
                  const days = getDaysUntilClose(closingDate);
                  const urgencyClass = getUrgencyClass(days);
                  const closingLabel = formatClosingLabel(closingDate);
                  const value = formatCurrency(getPriceEstimation(tender));

                  return (
                    <div
                      key={tenderId}
                      className="kanban-card"
                      draggable
                      onDragStart={(e) => handleDragStart(e, tenderId)}
                      onDragEnd={handleDragEnd}
                      onClick={() => navigate(tender.analysisStatus === 'complete' ? `/responses/${encodeURIComponent(tenderId)}` : `/tender/${encodeURIComponent(tenderId)}`)}
                    >
                      <div className="kanban-card-title">{tender.title || 'Untitled'}</div>
                      <div className="kanban-card-org">{getOrgName(tender)}</div>
                      <div className="kanban-card-meta">
                        {closingLabel && (
                          <span className={`kanban-card-date ${urgencyClass}`}>
                            <Clock size={11} />
                            {closingLabel}
                          </span>
                        )}
                        <span className="kanban-card-value">{value} CAD</span>
                      </div>
                      <KanbanProgressBar tenderId={tenderId} tender={tender} />
                      {(rec || tender.analysisStatus === 'complete') && (
                        <div className="kanban-card-footer">
                          {rec && (
                            <span className={`kanban-card-rec rec-${rec}`}>
                              {rec.toUpperCase()}
                            </span>
                          )}
                          {/* FORGE sourcing status (funnel readiness), when in FORGE */}
                          {forgeIds.has(tenderId) && forgeStatus[tenderId] && (() => {
                            const m = statusMeta(forgeStatus[tenderId]);
                            return <span className="kanban-card-rec" style={{ background: `${m.color}22`, color: m.color }}>{m.label}</span>;
                          })()}
                          {/* Discovery → FORGE: only once the tender is analysed */}
                          {tender.analysisStatus === 'complete' && (
                            forgeIds.has(tenderId) ? (
                              <button
                                className="kanban-forge-btn kanban-forge-btn-in"
                                title="In FORGE — click to open the cockpit"
                                onClick={(e) => { e.stopPropagation(); navigate(`/forge?tender=${encodeURIComponent(tenderId)}`); }}
                              >
                                In FORGE ✓
                              </button>
                            ) : (
                              <button
                                className="kanban-forge-btn"
                                title="Build the bid in FORGE"
                                onClick={(e) => { e.stopPropagation(); forgeAPI.enterForge(tenderId, tender.title || '').catch(() => {}); setForgeIds((s) => new Set(s).add(tenderId)); navigate(`/forge?tender=${encodeURIComponent(tenderId)}`); }}
                              >
                                Send to FORGE <ArrowRight size={11} />
                              </button>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Right panel: Analyzed responses with search + sort
  const AnalyzedPanel = () => {
    // Filter analyzed tenders by search
    const filteredAnalyzed = analyzedTenders.filter(tender => {
      if (!analyzedSearch) return true;
      const s = analyzedSearch.toLowerCase();
      return (
        tender.title?.toLowerCase().includes(s) ||
        getOrgName(tender).toLowerCase().includes(s) ||
        (tender.tender_id || '').toLowerCase().includes(s)
      );
    });

    // Sort
    const sortedAnalyzed = [...filteredAnalyzed].sort((a, b) => {
      switch (analyzedSort) {
        case 'oldest':
          return new Date(a.analyzedAt || 0) - new Date(b.analyzedAt || 0);
        case 'az':
          return (a.title || '').localeCompare(b.title || '');
        case 'za':
          return (b.title || '').localeCompare(a.title || '');
        case 'rec': {
          const order = { bid: 0, consider: 1, pass: 2 };
          const ra = getRecommendation(a) || 'consider';
          const rb = getRecommendation(b) || 'consider';
          return (order[ra] ?? 3) - (order[rb] ?? 3);
        }
        case 'newest':
        default:
          return new Date(b.analyzedAt || 0) - new Date(a.analyzedAt || 0);
      }
    });

    return (
      <div className="split-right">
        <div className="split-right-header">
          <h3>Analyzed</h3>
          <span className="split-right-count">({analyzedTenders.length})</span>
        </div>

        {analyzedTenders.length > 0 && (
          <div className="analyzed-controls">
            <input
              type="search"
              className="analyzed-search"
              placeholder="Search..."
              value={analyzedSearch}
              onChange={(e) => setAnalyzedSearch(e.target.value)}
            />
            <select
              className="analyzed-sort"
              value={analyzedSort}
              onChange={(e) => setAnalyzedSort(e.target.value)}
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="az">A-Z</option>
              <option value="za">Z-A</option>
              <option value="rec">Recommendation</option>
            </select>
          </div>
        )}

        {analyzedTenders.length === 0 ? (
          <div className="analyzed-empty">
            <p>No analyses yet</p>
            <span>Run analysis on a tender to see results here</span>
          </div>
        ) : sortedAnalyzed.length === 0 ? (
          <div className="analyzed-empty">
            <p>No matches</p>
            <span>Try a different search term</span>
          </div>
        ) : (
          <div className="analyzed-list">
            {sortedAnalyzed.map(tender => {
              const tenderId = tender.id || tender.tender_id || tender.reference_number;
              const rec = getRecommendation(tender);
              const orgName = getOrgName(tender);
              const analyzedDate = tender.analyzedAt
                ? format(new Date(tender.analyzedAt), 'MMM d, yyyy')
                : null;

              return (
                <div
                  key={tenderId}
                  className="response-card-mini"
                  onClick={() => navigate(`/responses/${encodeURIComponent(tenderId)}`)}
                >
                  <div className="response-mini-title">
                    {tender.title || 'Untitled'}
                  </div>
                  {orgName && (
                    <div className="response-mini-org">{orgName}</div>
                  )}
                  <div className="response-mini-footer">
                    <div className="response-mini-left">
                      {rec && (
                        <span className={`kanban-card-rec rec-${rec}`}>
                          {rec.toUpperCase()}
                        </span>
                      )}
                      {analyzedDate && (
                        <span className="response-mini-date">{analyzedDate}</span>
                      )}
                    </div>
                    <span className="response-mini-link">
                      View <ExternalLink size={11} />
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="dashboard-container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading saved tenders...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-container">
        {/* Header Section */}
        <div className="bookmarks-header">
          <div className="header-content">
            <div className="header-title-row">
              <Bookmark size={28} className="header-icon" />
              <div>
                <h1 className="page-title">My Tenders</h1>
                <p className="page-subtitle">
                  {displayBookmarks.length === 0
                    ? "Track opportunities you're interested in"
                    : `${displayBookmarks.length} tender${displayBookmarks.length !== 1 ? 's' : ''}, ${analyzedTenders.length} analyzed`
                  }
                  {' '}{getSyncIndicator()}
                </p>
                {syncError && <p className="sync-error">{syncError}</p>}
                {importStatus && <p className="import-status">{importStatus}</p>}
              </div>
            </div>
          </div>

          <div className="header-actions">
            {/* View Toggle */}
            <div className="view-toggle">
                {displayBookmarks.length > 0 && (
                  <>
                    <button
                      className={`view-toggle-btn ${viewMode === 'kanban' ? 'active' : ''}`}
                      onClick={() => setViewMode('kanban')}
                      title="Board view"
                    >
                      <LayoutGrid size={16} />
                      Board
                    </button>
                    <button
                      className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                      onClick={() => setViewMode('list')}
                      title="List view"
                    >
                      <LayoutList size={16} />
                      List
                    </button>
                  </>
                )}
                <button
                  className={`view-toggle-btn ${viewMode === 'team' ? 'active' : ''}`}
                  onClick={() => setViewMode('team')}
                  title="Team feed"
                >
                  <Users size={16} />
                  Team
                </button>
              </div>

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".json"
              style={{ display: 'none' }}
            />

            <button
              className="action-btn-header import-btn"
              onClick={handleImportClick}
              title="Import bookmarks from file"
            >
              <Upload size={16} />
              Import
            </button>

            <button
              className="action-btn-header export-btn"
              onClick={handleExport}
              disabled={displayBookmarks.length === 0}
              title="Export bookmarks to file"
            >
              <Download size={16} />
              Export
            </button>

            {isAuthenticated && (
              <button
                className="action-btn-header refresh-btn"
                onClick={handleRefresh}
                disabled={syncing}
                title="Refresh from cloud"
              >
                <RefreshCw size={16} className={syncing ? 'spinning' : ''} />
                Refresh
              </button>
            )}

            {displayBookmarks.length > 0 && (
              <button
                className="clear-all-btn"
                onClick={() => {
                  if (window.confirm('Are you sure you want to clear all saved tenders? This cannot be undone.')) {
                    clearAllBookmarks();
                  }
                }}
              >
                <Trash2 size={16} />
                Clear All
              </button>
            )}
          </div>
        </div>

        {viewMode === 'team' ? (
          <TeamFeed />
        ) : displayBookmarks.length === 0 ? (
          <div className="empty-state-card">
            <div className="empty-state-content">
              <div className="empty-icon-wrapper">
                <Bookmark size={48} strokeWidth={1.5} />
              </div>
              <h2>No saved tenders yet</h2>
              <p>Save tenders from the search page to keep track of opportunities you're interested in bidding on.</p>
              <Link to="/dashboard" className="btn-primary">
                <Search size={18} />
                Browse Tenders
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        ) : (
          <div className="my-tenders-split">
            {/* Left Panel: All tenders */}
            <div className="split-left">
              {viewMode === 'kanban' ? (
                <KanbanBoard />
              ) : (
                <div className="tenders-section">
                  <div className="search-controls">
                    <form className="search-bar" onSubmit={(e) => e.preventDefault()}>
                      <Search className="search-icon" />
                      <input
                        type="search"
                        placeholder="Search saved tenders..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      <button type="submit" className="stripe-button">
                        Search
                      </button>
                    </form>
                  </div>

                  <div className="list-filter-bar">
                    <div className="list-filter-toggle">
                      <button
                        className={`list-filter-btn ${listFilter === 'all' ? 'active' : ''}`}
                        onClick={() => setListFilter('all')}
                      >
                        All ({displayBookmarks.length})
                      </button>
                      <button
                        className={`list-filter-btn ${listFilter === 'analyzed' ? 'active' : ''}`}
                        onClick={() => setListFilter('analyzed')}
                      >
                        Analyzed ({analyzedTenders.length})
                      </button>
                      <button
                        className={`list-filter-btn ${listFilter === 'saved' ? 'active' : ''}`}
                        onClick={() => setListFilter('saved')}
                      >
                        Saved ({displayBookmarks.length - analyzedTenders.length})
                      </button>
                    </div>
                    <span className="list-filter-count">
                      {filteredBookmarks.length} tender{filteredBookmarks.length !== 1 ? 's' : ''}
                    </span>
                  </div>

                  {/* Dashboard-style chip row — status, categories, dates */}
                  <div className="saved-filter-row">
                    <TenderFilterChips
                      filters={listFilters}
                      onChange={setListFilters}
                    />
                  </div>

                  <div className="tender-results">
                    {paginatedBookmarks.map((tender) => {
                      const tenderId = tender.id || tender.tender_id || tender.reference_number;
                      return (
                        <SavedTenderCard
                          key={tenderId}
                          tender={tender}
                          isExpanded={expandedId === tenderId}
                          onToggleExpand={handleToggleExpand}
                          analysisState={getAnalysisStateForCard(tenderId)}
                          onRunAnalysis={handleRunAnalysis}
                          onNotesChange={handleNotesChange}
                          inForge={forgeIds.has(tenderId)}
                          onEnterForge={() => setForgeIds((s) => new Set(s).add(tenderId))}
                          onRemoveFromForge={handleRemoveFromForge}
                        />
                      );
                    })}
                  </div>

                  {totalPages > 1 && (
                    <div className="pagination">
                      <button
                        className="page-btn"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                      <div className="page-numbers">
                        {[...Array(totalPages)].map((_, i) => (
                          <button
                            key={i + 1}
                            className={`page-number ${currentPage === i + 1 ? 'active' : ''}`}
                            onClick={() => setCurrentPage(i + 1)}
                          >
                            {i + 1}
                          </button>
                        ))}
                      </div>
                      <button
                        className="page-btn"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right Panel: Analyzed Responses */}
            <AnalyzedPanel />
          </div>
          )}
      </div>
    </div>
  );
};

export default MyTenders;
